import classNames from 'classnames'
import { useParams } from 'react-router-dom'

import { Control } from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/Control'
import { ControlSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/ControlSkeleton'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'

import { useMyMonitoringGetControlListWithState } from '@apps-common/apis/my-monitoring/control/get-control-list-with-state'
import { useGetUnitControlListWithState } from '@apps-common/apis/portal/control'
import { ModalButton } from '@apps-common/components/modals'
import { useControlsContext } from '@apps-common/contexts/ControlContext'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitControlWithStateModel } from '@apps-common/types/portal/unit-control'
import { useIonModal } from '@ionic/react'
import { useEffect, useMemo } from 'react'
import { ControlModifyModal } from '../../pages/controls/pages/modify/modals'
import { NoControlsAddButton, NoControlsCard } from './control-buttons'
import { ControlsModal } from './ControlsModal'

interface IProps {
  loading?: boolean
  layoutPrefix: string
  unitId?: string
  isActivated: boolean
  hideCreate?: boolean
  registerRefetch?: (caller: { id: string; refetch: () => void }) => void
  showDefaultControlOptions?: boolean
}

export const UnitControls = ({
  loading,
  layoutPrefix,
  unitId,
  isActivated,
  hideCreate,
  registerRefetch,
  showDefaultControlOptions
}: IProps) => {
  const useGetControls = useProjectHook(useGetUnitControlListWithState, useMyMonitoringGetControlListWithState)
  const controlsContext = useControlsContext()

  const containerClasses = useProjectHook('bg-surface-2/30 border-border', 'bg-ctx-surface-2/30 border-ctx-border')
  const bgSurfaceClasses = useProjectHook('bg-surface', 'bg-ctx-surface')
  const bgSurface2Classes = useProjectHook('bg-surface-2', 'bg-ctx-surface-2')
  const borderColorClasses = useProjectHook('border-border', 'border-ctx-border')
  const textClasses = useProjectHook('text-text', 'text-ctx-text')

  const { isLoading, isFetching, isError, data, onControlStateUpdated, refetch } = useGetControls({
    config: { enabled: !!unitId },
    unitId: unitId || ''
  })
  const { status } = useParams<{ status: string }>()

  const [present, dismiss] = useIonModal(ControlModifyModal, {
    componentProps: {
      unitId
    },
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  const displayCount = 1
  // const navigation = useNavigation()

  useEffect(() => {
    if (registerRefetch) {
      registerRefetch({ id: 'manage-unit-controls', refetch })
    }
  }, [])

  const displayControl = useMemo(() => data?.find((c: IUnitControlWithStateModel) => c.isDefault) || data?.[0], [data])
  const remainingControls = useMemo(
    () => data?.filter((c: IUnitControlWithStateModel) => c.unitControlId !== displayControl?.unitControlId) || [],
    [data, displayControl?.unitControlId]
  )

  const handleAction = (action: string) => {
    if (action === 'create') {
      present({
        enterAnimation: modalSlideUpEnterAnimation,
        leaveAnimation: modalSlideUpLeaveAnimation
      })
    }
  }

  loading = loading || isLoading || isFetching || isError || !data

  return (
    <div className={classNames('relative !p-[2px] backdrop-blur border rounded-[35px]', containerClasses)}>
      <div className='relative'>
        <div className='flex flex-wrap items-start justify-start gap-x-[10px]'>
          <div className='w-[calc(50%-5px)]'>
            {loading && Array.from(Array(displayCount).keys()).map((k) => <ControlSkeleton key={`control-list-item-skeleton-${k}`} />)}
            {!loading && !isFetching && data?.length === 0 && (
              <>
                {!hideCreate && <NoControlsAddButton unitStatus={status} unitId={unitId || ''} isActivated={isActivated} />}
                {hideCreate && <NoControlsCard />}
              </>
            )}

            {!loading && displayControl && (
              <div key={`${layoutPrefix}-control-list-item-${displayControl.unitControlId}`} className='flex'>
                <Control
                  // navigation={navigation}
                  unitStatus={status}
                  isActivated={isActivated}
                  // colors={colors}
                  control={displayControl}
                  onStateUpdatedCallback={onControlStateUpdated}
                  idPrefix={`closed-${layoutPrefix}`}
                  showDefaultControlOptions={showDefaultControlOptions}
                />
              </div>
            )}
          </div>

          <div className='relative z-[0] w-[calc(50%-5px)]'>
            {loading && (
              <div className='relative w-full pt-[100%] rounded-[35px] bg-skeleton animate-pulse'>
                <div className='absolute top-0 left-0 px-[15px] py-[10px] w-full h-[100%] flex flex-col items-between justify-start gap-[5px] '>
                  <div className='flex items-center justify-between text-[0.85em] jost w-full px-[7px]'>
                    <div className='h-[16px] bg-skeleton-hi rounded-xl w-[60%]' />
                    <div className='h-[20px] w-[20px] rounded-full bg-skeleton-hi' />
                  </div>
                  <div className='grow flex flex-col items-center justify-center gap-[5px] sm:gap-[10px]'>
                    {!hideCreate && (
                      <>
                        <div className='w-full bg-skeleton-hi disabled:opacity-40 h-[20px] sm:h-[28px] rounded-full' />
                        <div className='w-full bg-skeleton-hi disabled:opacity-40 h-[20px] sm:h-[28px] rounded-full' />
                      </>
                    )}
                    {hideCreate && <div className='w-full bg-skeleton-hi disabled:opacity-40 grow rounded-[20px] my-[4px]' />}
                  </div>
                </div>
              </div>
            )}

            {!loading && (
              <div className={classNames('relative z-0 w-full pt-[100%] rounded-[30px]', bgSurfaceClasses)}>
                <div className='absolute top-0 left-0 px-[10px] sm:px-[13px] py-[8px] sm:py-[12px] w-full h-[100%] flex flex-col items-between justify-between gap-[2px] '>
                  <div className='flex items-center justify-between text-[0.8em] jost w-full px-[7px]'>
                    Controls <commonIcons.control />
                  </div>
                  <div className='grow flex flex-col items-center justify-center gap-[10px]'>
                    {!hideCreate && (
                      <>
                        <button
                          aria-label='open-all-controls'
                          type='button'
                          disabled={status === UNIT_STATUS_CANCELLED.value || loading}
                          className={classNames(
                            'w-full text-text disabled:opacity-40 rounded-[20px] flex items-center justify-between text-[0.85em] sm:pr-[3px] pl-[15px] py-1',
                            bgSurface2Classes
                          )}
                          onClick={() =>
                            present({
                              enterAnimation: modalSlideUpEnterAnimation,
                              leaveAnimation: modalSlideUpLeaveAnimation
                            })
                          }
                        >
                          Add
                          <div
                            className={classNames(
                              'w-[20px] h-[20px] border rounded-full flex items-center justify-center',
                              bgSurfaceClasses,
                              borderColorClasses
                            )}
                          >
                            <commonIcons.add />
                          </div>
                        </button>

                        <ModalButton
                          component={ControlsModal}
                          disabled={!data || data?.length < 1 || loading}
                          componentProps={{
                            status,
                            isActivated,
                            unitId,
                            // controls: data,
                            // onControlStateUpdated,
                            layoutPrefix,
                            hideCreate,
                            controlsContext,
                            showDefaultControlOptions
                            // navigation
                          }}
                          classes='w-full'
                          confirmCallback={(data) => handleAction(data.action)}
                          enterAnimation={modalSlideUpEnterAnimation}
                          leaveAnimation={modalSlideUpLeaveAnimation}
                          backdropDismiss
                        >
                          <div
                            className={classNames(
                              'relative w-full disabled:opacity-40 rounded-[20px] flex items-center justify-between text-[0.85em] p-1 sm:pl-[3px] py-1',
                              bgSurface2Classes,
                              remainingControls?.length > 0 && 'pr-[20px]',
                              remainingControls?.length === 0 && 'pr-[15px]'
                            )}
                          >
                            <div
                              className={classNames(
                                'w-[20px] h-[20px] border rounded-full flex items-center justify-center',
                                bgSurfaceClasses,
                                borderColorClasses
                              )}
                            >
                              <commonIcons.arrowAngledLeft />
                            </div>
                            More
                            {remainingControls?.length > 0 && (
                              <div
                                className={classNames(
                                  'top-[-5px] right-[-5px] min-w-[20px] absolute border p-[2px] rounded-full text-[0.8em]',
                                  bgSurfaceClasses,
                                  borderColorClasses,
                                  textClasses
                                )}
                              >
                                {remainingControls?.length}
                              </div>
                            )}
                          </div>
                        </ModalButton>
                      </>
                    )}

                    {hideCreate && (
                      <ModalButton
                        component={ControlsModal}
                        disabled={!data || data?.length < 1 || loading}
                        componentProps={{
                          status,
                          isActivated,
                          unitId,
                          controls: data,
                          onControlStateUpdated,
                          layoutPrefix,
                          hideCreate,
                          controlsContext,
                          showDefaultControlOptions
                          // navigation
                        }}
                        classes='w-full grow'
                        confirmCallback={(data) => handleAction(data.action)}
                        enterAnimation={modalSlideUpEnterAnimation}
                        leaveAnimation={modalSlideUpLeaveAnimation}
                        backdropDismiss
                      >
                        <div
                          className={classNames(
                            'h-[90%] relative w-full border disabled:opacity-40 rounded-[20px] flex flex-col items-center justify-center gap-[5px] text-[0.8rem] leading-[0.8rem] p-[2px] py-[4px] my-[4px]',
                            bgSurface2Classes,
                            borderColorClasses,
                            textClasses
                          )}
                        >
                          <div
                            className={classNames(
                              'w-[28px] h-[28px] border rounded-full flex items-center justify-center',
                              bgSurface2Classes,
                              borderColorClasses
                            )}
                          >
                            <commonIcons.arrowAngledLeft />
                          </div>
                          More
                          <br />
                          {remainingControls?.length > 0 && (
                            <div
                              className={classNames(
                                'top-[3px] right-[3px] min-w-[25px] h-[25px] flex items-center justify-center absolute border p-[2px] rounded-full text-[0.8em]',
                                bgSurfaceClasses,
                                borderColorClasses
                              )}
                            >
                              {remainingControls?.length}
                            </div>
                          )}
                        </div>
                      </ModalButton>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
